import { mapMutations } from 'vuex';

import webSocketMixin from '@/mixins/webSocketMixin';

const FLUSH_INTERVAL_MS = 2000;

/**
 * Shared by the traffic maps (Google and SwissTopo variants) to feed the
 * `live/heartbeats` store from the heartbeat WebSocket.
 *
 * The back-end broadcasts one WebSocket message per vehicle per publish cycle.
 * Committing each message individually re-renders every marker on the map for
 * every single vehicle update, which is O(vehicles²) work per cycle. Incoming
 * updates are therefore buffered in plain (non-reactive) Maps and committed in
 * one batch per FLUSH_INTERVAL_MS.
 */
export default {
  mixins: [webSocketMixin],

  socket: {
    namespace: '/api/heartbeat/',
    events: {
      message(event) {
        const data = JSON.parse(event.data);

        if (data instanceof Array) {
          // Full snapshot sent on (re)connection: it supersedes anything buffered
          this.$_heartbeatUpserts.clear();
          this.$_heartbeatDeletes.clear();
          this.setHeartbeats(data);
          return;
        }

        // For a given username only the most recent event matters, so each event
        // evicts the username from the opposite buffer.
        switch (data.type) {
          case 'model.create':
          case 'model.change':
            this.$_heartbeatDeletes.delete(data.instance.username);
            this.$_heartbeatUpserts.set(data.instance.username, data.instance);
            break;
          case 'model.delete':
            this.$_heartbeatUpserts.delete(data.instance.username);
            this.$_heartbeatDeletes.set(data.instance.username, data.instance);
            break;
        }
      },
    },
  },

  created() {
    this.$_heartbeatUpserts = new Map();
    this.$_heartbeatDeletes = new Map();
    this.$_heartbeatFlushInterval = setInterval(
      this.$_flushHeartbeats,
      FLUSH_INTERVAL_MS
    );
  },

  beforeDestroy() {
    clearInterval(this.$_heartbeatFlushInterval);
  },

  methods: {
    ...mapMutations('live', [
      'setHeartbeats',
      'setHeartbeat',
      'removeHeartbeat',
    ]),

    $_flushHeartbeats() {
      if (this.$_heartbeatUpserts.size) {
        this.setHeartbeats([...this.$_heartbeatUpserts.values()]);
        this.$_heartbeatUpserts.clear();
      }
      if (this.$_heartbeatDeletes.size) {
        this.$_heartbeatDeletes.forEach(heartbeat =>
          this.removeHeartbeat(heartbeat)
        );
        this.$_heartbeatDeletes.clear();
      }
    },
  },
};
